import React, { useRef } from 'react';
import styled from 'styled-components';
import SelectSearch from 'react-select-search';
import OrganizationList from './OrganizationList';
import { colors, extraFilters } from '../constants.js';
import CheckBox from './CheckBox';
import translate from '../utils/translate';
import { Icon, SubTitle } from './basic';
import {
  faSpinner,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import ExtraFilterIcon from './ExtraFilterIcon';
import FeedbackLink from './FeedbackLink';
import CustomTooltip from './ToolTip';

const Select = styled(SelectSearch)`
  font-size: 1.2rem;
  max-width: 250px;
  width: 100%;
  font-family: inherit;
  position: relative;
  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 50%;
    right: 16px;
    margin-top: -3px;
    border-style: solid;
    border-color: #999 transparent transparent transparent;
    border-width: 6px 4px 0 4px;
  }
  & * {
    font-family: inherit;
  }
  & input {
    max-width: 250px;
    width: 100%;
    border: 2px solid #8651c7;
    font-size: 1.2rem;
    padding: 6px 12px;
    font-weight: bold;
  }
  & > div:nth-child(2) {
    position: relative;
    max-width: 250px
    width: 100%;
    margin: 0;
    padding: 0;
    z-index: 1;
  }
  & ul {
    position: absolute;
    top: 0;
    left: -32px;
    width: 100%;
    list-style: none;
    max-height: min(320px, 50vh);
    overflow-y: auto;
    margin: 0:
    padding: 0;
    background: #fff;
    box-shadow: 1px 2px 3px #0003;
  }
  & li {
    margin: 0;
    padding: 0;
  }
  & button {
    display: block;
    width: 100%;
    height: 36px;
    margin: 0;
    padding: 0 16px;
    background: #fff;
    border: none;
    outline: none;
    font-size: 1rem;
    text-align: left;
    cursor: pointer;
  }
  & button:hover {
    background: #eee;
  }
  & button.is-highlighted,
  & button.is-highlighted:hover {
    background: ${colors.secondary};
    color: #fff;
}
`;

const Waiting = styled.h2`
  margin-top: 64px;
  margin-bottom: 64px;
  font-size: 1.4rem;
  color: ${colors.primary};
`;

const SearchResults = styled.div`
  margin-top: 64px;
`;

const FilterGroup = styled.div`
  margin-bottom: 36px;
  @media (min-width: 600px) {
    display: flex;
  }
`;

const FilterTitle = styled.div`
  margin-bottom: 12px;
  font-weight: bold;
  @media (min-width: 600px) {
    width: 220px;
    flex-shrink: 0;
    float: left;
  }
`;

const FilterCount = styled.span`
  margin-left: 4px;
  color: ${colors.primary};
`;

const Filters = styled.div``;

const ExtraFilterGroup = styled.div`
  margin: 32px 0 16px;
  @media (min-width: 600px) {
    margin: 64px 0 16px;
    display: flex;
  }
`;

const ExtraFilters = styled.div`
  @media (min-width: 600px) {
    display: flex;
    flex-wrap: wrap;
    & > label {
      margin-right: 24px;
    }
  }
`;

const ExtraFilterLabel = styled.span`
  margin-left: 5px;
`;
const ClearAllWrapper = styled.div`
  height: 40px;
`;

const ClearAllButton = styled.button`
  display: inline-block;
  font: inherit;
  font-size: inherit;
  color: ${colors.secondary};
  background: none;
  border: none;
  margin: 0;
  padding: 0;
  font-weight: bold;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
  &:focus {
    text-decoration: underline;
  }
`;

function SearchView({ apiHandler }) {
  const {
    status,
    selectedMunicipality,
    municipalities,
    filteredServices,
    fetchServicesFromMunicipality,
    resetFilters,
    activeFilters,
    toggleFilter,
    municipalityServices,
    organizationData,
    toggleExtraFilter,
    activeExtraFilters,
    clearFilters,
    logMunicipalityChange,
  } = apiHandler;

  const resultRef = useRef();

  const handleMunicipalityChange = (value) => {
    if (value) {
      resetFilters();
      fetchServicesFromMunicipality(value);
      logMunicipalityChange(value);
    }
  };

  const municipalityOptions = municipalities.map(
    (municipality) => ({
      value: municipality.code,
      name: municipality.names[0].value,
    }),
  );

  const filterOptions = (options) => (value) =>
    options.filter((item) =>
      item.name
        .toLowerCase()
        .startsWith(value.toLowerCase()),
    );

  function scrollToResults() {
    if (
      window.innerWidth < 600 ||
      window.innerHeight < 450
    ) {
      window.scrollTo({
        top: resultRef.current.offsetTop - 20,
        behavior: 'smooth',
      });
    }
  }

  function handleFilterToggle(event) {
    toggleFilter(event.target.name);
    if (event.target.checked) {
      scrollToResults();
    }
  }

  function handleExtraFilterToggle(event) {
    toggleExtraFilter(event.target.name);
    if (event.target.checked) {
      scrollToResults();
    }
  }

  return (
    <div>
      <SubTitle id="filter-label">
        Missä kunnassa tarvitset palvelua?
      </SubTitle>

      <Select
        options={municipalityOptions}
        search
        filterOptions={filterOptions}
        aria-labelledby="filter-label"
        id="municipalities"
        name="municipalities"
        value={selectedMunicipality}
        onChange={handleMunicipalityChange}
        placeholder="Valitse kunta"
        disabled={
          status === 'loading' || status === 'loading-extra'
        }
      />
      {['loading', 'loading-extra'].includes(status) && (
        <Waiting>
          <Icon icon={faSpinner} spin={true} size="lg" />
          Haetaan kaikkia alueen palveluita...
        </Waiting>
      )}

      {status === 'ready' && selectedMunicipality !== '' && (
        <SearchResults id="search-results">
          <SubTitle>Mitä palvelua etsit?</SubTitle>
          {Object.entries(municipalityServices).map(
            ([name, filterLists]) => {
              // remove empty filters from checkboxes
              const filters = Object.keys(
                filterLists,
              ).filter(
                (item) =>
                  municipalityServices[name][item].data
                    .length,
              );
              return !filters.length ? null : (
                <FilterGroup
                  key={name}
                  className="filter-group"
                >
                  <FilterTitle>{name}</FilterTitle>
                  <Filters>
                    {filters.map((item) => (
                      <CheckBox
                        key={item}
                        name={item}
                        checked={activeFilters.includes(
                          item,
                        )}
                        onChange={handleFilterToggle}
                      >
                        <CustomTooltip
                          toolTipText={
                            municipalityServices[name][item]
                              .description
                          }
                        >
                          {item}{' '}
                        </CustomTooltip>
                        <FilterCount>
                          (
                          {status === 'ready'
                            ? municipalityServices[name][
                                item
                              ].data.length || 0
                            : '…'}
                          )
                        </FilterCount>
                      </CheckBox>
                    ))}
                  </Filters>
                </FilterGroup>
              );
            },
          )}
          <ExtraFilterGroup
            key="extra-filters"
            className="filter-group"
          >
            <FilterTitle>Rajaa hakua</FilterTitle>
            <ExtraFilters>
              {extraFilters.map((element) => (
                <CheckBox
                  key={element}
                  name={element}
                  checked={activeExtraFilters.includes(
                    element,
                  )}
                  onChange={handleExtraFilterToggle}
                >
                  <ExtraFilterIcon type={element} />
                  <ExtraFilterLabel>
                    {translate(element)}
                  </ExtraFilterLabel>
                </CheckBox>
              ))}
            </ExtraFilters>
          </ExtraFilterGroup>

          <ClearAllWrapper id="clear-wrapper">
            {(activeFilters.length > 0 ||
              activeExtraFilters.length > 0) && (
              <ClearAllButton onClick={clearFilters}>
                <Icon icon={faTimes} />
                Tyhjennä rajaukset
              </ClearAllButton>
            )}
          </ClearAllWrapper>

          {/* Results */}
          {status === 'ready' &&
            (filteredServices?.length > 0 ? (
              <OrganizationList
                ref={resultRef}
                services={filteredServices}
                organizations={organizationData}
                activeSelections={[
                  ...activeFilters,
                  ...activeExtraFilters,
                ]}
              />
            ) : (
              'Hakuasi vastaavia palveluja ei löytynyt'
            ))}
          <FeedbackLink />
        </SearchResults>
      )}
    </div>
  );
}

export default SearchView;
